import { useEffect, useRef } from 'react';

const CONFETTI_COLORS = ['#d4af37', '#f5c542', '#e8b04b', '#2f9e6f', '#34d399', '#faf3e0'];

/**
 * Zero-dependency hardware-accelerated HTML5 Canvas particle confetti shower.
 * Runs on a single canvas via requestAnimationFrame with 3D paper flipping,
 * fluttering sway, and locked 60 FPS performance.
 */
export function ConfettiShower() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvas.getContext('2d');
    } catch {
      return;
    }
    if (!ctx) return;

    let animId: number;
    let width = 0;
    let height = 0;

    const updateSize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    updateSize();
    window.addEventListener('resize', updateSize);

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      width: number;
      height: number;
      color: string;
      rotation: number;
      rotationSpeed: number;
      oscillation: number;
      oscillationSpeed: number;
      opacity: number;
      isCircle: boolean;
    }

    const particleCount = 110;
    const particles: Particle[] = Array.from({ length: particleCount }).map(() => ({
      x: Math.random() * width,
      y: Math.random() * -height * 0.5 - 20,
      vx: (Math.random() - 0.5) * 3,
      vy: Math.random() * 3 + 2.4,
      width: Math.random() * 8 + 6,
      height: Math.random() * 6 + 4,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.14,
      oscillation: Math.random() * Math.PI * 2,
      oscillationSpeed: Math.random() * 0.08 + 0.03,
      opacity: 1,
      isCircle: Math.random() > 0.65,
    }));

    const startTime = performance.now();
    const duration = 5000;

    const render = (now: number) => {
      const elapsed = now - startTime;
      ctx.clearRect(0, 0, width, height);

      let aliveCount = 0;

      for (const p of particles) {
        p.x += p.vx + Math.sin(p.oscillation) * 1.2;
        p.y += p.vy;
        p.vy += 0.04;
        p.rotation += p.rotationSpeed;
        p.oscillation += p.oscillationSpeed;

        if (elapsed > duration - 1200) {
          p.opacity = Math.max(0, (duration - elapsed) / 1200);
        } else if (p.y > height - 60) {
          p.opacity = Math.max(0, (height - p.y) / 60);
        }

        if (p.y < height + 40 && p.opacity > 0) {
          aliveCount++;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.scale(Math.cos(p.oscillation), 1);
          ctx.globalAlpha = p.opacity;
          ctx.fillStyle = p.color;

          if (p.isCircle) {
            ctx.beginPath();
            ctx.arc(0, 0, p.width * 0.45, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.fillRect(-p.width * 0.5, -p.height * 0.5, p.width, p.height);
          }
          ctx.restore();
        }
      }

      if (aliveCount > 0 && elapsed < duration) {
        animId = requestAnimationFrame(render);
      }
    };

    animId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 9990,
      }}
      aria-hidden="true"
    />
  );
}
